import { useQuery } from "@tanstack/react-query";

import {
  fetchCampaignEmailSends,
  fetchEngagementEmailSends,
  funnelStage,
  tallyFunnel,
  type EmailSend,
  type FunnelStage,
} from "../api/emailSends";
import { Card } from "./Card";

/**
 * Email funnel results — customer-safe view (status + sent/clicked/submitted
 * timestamps + display name only). Operator-only data (token, relay, raw email)
 * never reaches this component: it isn't on the EmailSend schema.
 *
 * Mount with EITHER engagementId OR campaignId.
 */
export function EmailResultsPanel({
  engagementId,
  campaignId,
  live,
}: {
  engagementId?: string;
  campaignId?: string;
  /** Poll while the source is actively sending. */
  live?: boolean;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: engagementId
      ? ["engagement-email-sends", engagementId]
      : ["campaign-email-sends", campaignId],
    queryFn: () =>
      engagementId
        ? fetchEngagementEmailSends(engagementId)
        : fetchCampaignEmailSends(campaignId!),
    enabled: !!engagementId || !!campaignId,
    refetchInterval: live ? 5000 : false,
  });

  const sends = data?.items ?? [];
  const counts = tallyFunnel(sends);

  return (
    <Card title="Email results" accentColor="var(--accent)">
      {isLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading results…</div>
      ) : isError ? (
        <div style={{ fontSize: 12, color: "var(--error)" }}>Failed to load email results.</div>
      ) : sends.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          No sends yet. Results appear here once the engagement dispatches lures.
        </div>
      ) : (
        <>
          {/* Funnel summary */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
            <FunnelStat label="Sent" value={counts.sent} color="var(--text-secondary)" />
            <FunnelStat label="Clicked" value={counts.clicked} color="var(--warning)" />
            <FunnelStat label="Submitted" value={counts.submitted} color="var(--error)" />
            {counts.send_failed > 0 && (
              <FunnelStat label="Failed" value={counts.send_failed} color="var(--text-muted)" />
            )}
          </div>

          {/* Per-target rows */}
          <div style={{ display: "flex", flexDirection: "column", border: "1px solid var(--border-subtle)", borderRadius: 8, overflow: "hidden" }}>
            <div style={rowHeaderStyle}>
              <span>Target</span>
              <span>Stage</span>
              <span style={{ textAlign: "right" }}>Sent</span>
              <span style={{ textAlign: "right" }}>Clicked</span>
              <span style={{ textAlign: "right" }}>Submitted</span>
            </div>
            {sends.map((s: EmailSend) => {
              const stage = funnelStage(s);
              return (
                <div key={`${s.engagement_id}:${s.target_id}`} style={rowStyle}>
                  <span style={{ fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.target_display_name}
                  </span>
                  <StageBadge stage={stage} />
                  <TimeCell iso={s.sent_at} />
                  <TimeCell iso={s.clicked_at} />
                  <TimeCell iso={s.submitted_at} />
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}

function StageBadge({ stage }: { stage: FunnelStage }) {
  const map: Record<FunnelStage, { label: string; color: string }> = {
    sent: { label: "SENT", color: "var(--text-secondary)" },
    clicked: { label: "CLICKED", color: "var(--warning)" },
    submitted: { label: "SUBMITTED", color: "var(--error)" },
    send_failed: { label: "FAILED", color: "var(--text-muted)" },
  };
  const { label, color } = map[stage];
  return (
    <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.1em", color }}>
      {label}
    </span>
  );
}

function FunnelStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: "6px 12px",
        borderRadius: 8,
        border: "1px solid var(--border-subtle)",
        minWidth: 72,
      }}
    >
      <span style={{ fontSize: 20, fontWeight: 600, color }}>{value}</span>
      <span style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)" }}>
        {label}
      </span>
    </div>
  );
}

function TimeCell({ iso }: { iso: string | null }) {
  return (
    <span style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 10.5, color: iso ? "var(--text-secondary)" : "var(--text-muted)" }}>
      {iso ? new Date(iso).toLocaleString() : "—"}
    </span>
  );
}

const rowHeaderStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.4fr 0.8fr 1fr 1fr 1fr",
  gap: 10,
  padding: "8px 12px",
  fontSize: 9.5,
  textTransform: "uppercase",
  letterSpacing: 0.8,
  color: "var(--text-muted)",
  fontWeight: 700,
  background: "var(--bg-secondary)",
  borderBottom: "1px solid var(--border-subtle)",
};

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.4fr 0.8fr 1fr 1fr 1fr",
  gap: 10,
  padding: "9px 12px",
  borderBottom: "1px solid var(--border-subtle)",
  alignItems: "center",
  fontSize: 11.5,
};
